import parse from "html-react-parser";
import React from "react";

// List of allowed HTML tags for stat labels
const defaultAllowedTags = [
  "b",
  "strong",
  "em",
  "i",
  "a",
  "u",
  "p",
  "ul",
  "li",
  "span",
  "br",
];

// Utility function to sanitize and parse HTML to React elements, allowing only specific tags
const sanitizeAndParseInlineHTML = (
  html: string,
  hasDarkBackground?: boolean,
  allowedTags: string[] = defaultAllowedTags,
): React.ReactNode => {
  if (!html) return null;

  const parsed = parse(html);

  function filter(node: any): any {
    if (typeof node === "string") return node;
    if (node && node.type && typeof node.type === "string") {
      if (!allowedTags.includes(node.type)) return null;
      // Special handling for <a>
      if (node.type === "a") {
        // Only allow safe hrefs
        const href = node.props?.href || "";
        if (
          href.startsWith("/") ||
          href.startsWith("https://") ||
          href.startsWith("http://")
        ) {
          const className = `underline-offset-2 ${hasDarkBackground ? "underline text-white hover:text-white" : "text-link hover:underline"}`;
          // Recursively filter children
          const children =
            node.props && node.props.children
              ? Array.isArray(node.props.children)
                ? node.props.children.map(filter)
                : filter(node.props.children)
              : null;
          return React.createElement(
            "a",
            { ...node.props, className },
            children,
          );
        } else {
          // Unsafe href, render only children
          return node.props && node.props.children
            ? Array.isArray(node.props.children)
              ? node.props.children.map(filter)
              : filter(node.props.children)
            : null;
        }
      }
      // Special handling for <br> (self-closing tag)
      if (node.type === "br") {
        return React.createElement("br");
      }
      // Recursively filter children for other allowed tags
      if (node.props && node.props.children) {
        return React.cloneElement(
          node,
          node.props,
          Array.isArray(node.props.children)
            ? node.props.children.map(filter)
            : filter(node.props.children),
        );
      }
    }
    return node;
  }

  if (Array.isArray(parsed)) {
    return parsed.map(filter);
  }
  return filter(parsed);
};

export default sanitizeAndParseInlineHTML;
